/**
 * ContactLink Component
 * A contact reference input component with search, open, and clear buttons
 * 
 * @description Provides a styled contact reference input for contact lookups:
 * - Search (magnifying glass): Open ContactPicker modal to search and select a contact
 * - Open (user): Open the selected contact in a new form/tab
 * - Clear (X): Remove the contact reference
 * 
 * This matches the FileLink pattern but for Contact references (FieldTypeId: 19).
 * 
 * @example
 * ```typescript
 * const contactLink = new ContactLink({
 *   id: 'referee-contact',
 *   label: 'Referee',
 *   onContactChange: (id, name) => console.log('Contact changed:', id, name)
 * });
 * await contactLink.initialize();
 * container.appendChild(contactLink.getElement());
 * ```
 */

import { IComponent } from './IComponent';
import { ContactPicker, type ContactPickerApiService } from './ContactPicker';
import { Logger } from '@/services/Logger';
import { ToasterService } from '@/services/ToasterService';

const logger = Logger.scope('ContactLink');

/**
 * Configuration options for the ContactLink component
 */
export interface ContactLinkOptions {
  /** Unique ID for the input element */
  id: string;
  
  /** Optional label displayed above the input */
  label?: string;
  
  /** Color variant for buttons */
  color?: 'primary' | 'secondary' | 'accent' | 'info' | 'success' | 'warning' | 'error';
  
  /** Size variant */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  
  /** Whether the input is disabled */
  disabled?: boolean;
  
  /** Whether the input is readonly (no search/clear, only open) */
  readonly?: boolean;
  
  /** Placeholder text when no contact is selected */
  placeholder?: string;
  
  /** Callback when contact changes */
  onContactChange?: (contactId: number | null, fullName: string | null) => void;
  
  /** Callback when contact is opened */
  onContactOpen?: (contactId: number) => void;
  
  /** Custom open handler (overrides default behavior) */
  customOpenHandler?: (contactId: number) => Promise<void>;
  
  /** Show label above input */
  showLabel?: boolean;
  
  /** Additional CSS classes for the container */
  containerClass?: string;
  
  /** Contact IDs to exclude from search results */
  excludeContactIds?: number[];
  
  /** API service for ContactPicker (tenant-specific) */
  apiService?: ContactPickerApiService;
  
  /** Hide the search button */
  hideSearchButton?: boolean;
  
  /** Hide the open button */
  hideOpenButton?: boolean;
  
  /** Hide the clear button */
  hideClearButton?: boolean;
}

/**
 * ContactLink Component - Contact reference input with search/open/clear buttons
 */
export class ContactLink implements IComponent {
  private options: ContactLinkOptions;
  private element: HTMLElement | null = null;
  private inputElement: HTMLInputElement | null = null;
  private contactId: number | null = null;
  private contactName: string | null = null;

  constructor(options: ContactLinkOptions) {
    this.options = {
      color: 'info',
      size: 'sm',
      disabled: false,
      readonly: false,
      placeholder: 'No contact selected',
      showLabel: false,
      hideSearchButton: false,
      hideOpenButton: false,
      hideClearButton: false,
      ...options
    };
  }

  /**
   * Initialize the component
   */
  public async initialize(): Promise<void> {
    this.element = this.render();
    this.bindEvents();
    logger.debug('ContactLink initialized', { id: this.options.id });
  }

  /**
   * Get the component's root element
   */
  public getElement(): HTMLElement {
    if (!this.element) {
      throw new Error('ContactLink not initialized. Call initialize() first.');
    }
    return this.element;
  }

  /**
   * Render into a container element
   */
  public renderTo(container: HTMLElement): void {
    if (!this.element) {
      throw new Error('ContactLink not initialized. Call initialize() first.');
    }
    container.appendChild(this.element);
  }

  /**
   * Get the current contact ID
   */
  public getContactId(): number | null {
    return this.contactId;
  }

  /**
   * Get the current contact name
   */
  public getContactName(): string | null {
    return this.contactName;
  }

  /**
   * Set the contact
   */
  public setContact(contactId: number | null, fullName: string | null): void {
    this.contactId = contactId;
    this.contactName = fullName;
    if (this.inputElement) {
      this.inputElement.value = fullName || '';
      this.inputElement.setAttribute('data-contact-id', contactId ? String(contactId) : '');
    }
    this.updateButtonStates();
  }

  /**
   * Check if a contact is set
   */
  public hasContact(): boolean {
    return this.contactId !== null && this.contactId > 0;
  }

  /**
   * Enable/disable the component
   */
  public setDisabled(disabled: boolean): void {
    this.options.disabled = disabled;
    this.updateButtonStates();
  }

  /**
   * Get the value for form collection (returns the contact ID)
   */
  public getValue(): number | null {
    return this.contactId;
  }

  /**
   * Set the value from form population
   */
  public setValue(contactId: number | null, displayValue?: string): void {
    this.setContact(contactId, displayValue || null);
  }

  /**
   * Destroy the component and clean up
   */
  public destroy(): void {
    if (this.element) {
      this.element.remove();
    }
    this.element = null;
    this.inputElement = null;
  }

  /**
   * Render the component HTML
   */
  private render(): HTMLElement {
    const { id, label, size, disabled, readonly, showLabel, containerClass, placeholder,
            hideSearchButton, hideOpenButton, hideClearButton } = this.options;

    const container = document.createElement('div');
    container.className = `contact-link-container ${containerClass || ''}`;

    // Label (if shown)
    if (showLabel && label) {
      const labelElement = document.createElement('label');
      labelElement.className = 'label py-0.5';
      labelElement.innerHTML = `<span class="label-text text-xs font-medium">${label}</span>`;
      container.appendChild(labelElement);
    }

    // Input group with buttons
    const buttonGroup = document.createElement('div');
    buttonGroup.className = 'join w-full';

    // Readonly contact name input
    const input = document.createElement('input');
    input.type = 'text';
    input.id = id;
    input.className = `input input-bordered input-${size} join-item flex-1`;
    input.readOnly = true;
    input.placeholder = placeholder || label || 'No contact selected';
    this.inputElement = input;
    buttonGroup.appendChild(input);

    // Search button (find contact)
    if (!readonly && !hideSearchButton) {
      const searchBtn = document.createElement('button');
      searchBtn.type = 'button';
      searchBtn.className = `btn btn-${size} join-item contact-link-search bg-base-100 hover:bg-base-200 border border-base-content/20`;
      searchBtn.innerHTML = '<i class="fas fa-search" style="color: #3b82f6;"></i>';
      searchBtn.title = 'Search for contact';
      searchBtn.disabled = disabled || false;
      buttonGroup.appendChild(searchBtn);
    }

    // Open button - blue user icon when enabled, grey when disabled
    if (!hideOpenButton) {
      const openBtn = document.createElement('button');
      openBtn.type = 'button';
      const openDisabled = disabled || !this.contactId;
      openBtn.className = `btn btn-${size} join-item contact-link-open bg-base-100 border border-base-content/20 ${openDisabled ? 'opacity-60 cursor-not-allowed' : 'hover:bg-base-200'}`;
      openBtn.innerHTML = `<i class="fas fa-user" style="color: ${openDisabled ? '#9ca3af' : '#3b82f6'};"></i>`;
      openBtn.title = 'Open contact';
      openBtn.dataset.disabled = openDisabled ? 'true' : 'false';
      buttonGroup.appendChild(openBtn);
    }

    // Clear button - red icon when enabled, grey when disabled
    if (!readonly && !hideClearButton) {
      const clearBtn = document.createElement('button');
      clearBtn.type = 'button';
      const clearDisabled = disabled || !this.contactId;
      clearBtn.className = `btn btn-${size} join-item contact-link-clear bg-base-100 border border-base-content/20 ${clearDisabled ? 'opacity-60 cursor-not-allowed' : 'hover:bg-base-200'}`;
      clearBtn.innerHTML = `<i class="fas fa-times" style="color: ${clearDisabled ? '#9ca3af' : '#ef4444'};"></i>`;
      clearBtn.title = 'Clear contact';
      clearBtn.dataset.disabled = clearDisabled ? 'true' : 'false';
      buttonGroup.appendChild(clearBtn);
    }

    container.appendChild(buttonGroup);

    return container;
  }

  /**
   * Bind event handlers
   */
  private bindEvents(): void {
    if (!this.element) return;

    // Search button click
    const searchBtn = this.element.querySelector('.contact-link-search');
    if (searchBtn) {
      searchBtn.addEventListener('click', () => this.handleSearch());
    }

    // Open button click
    const openBtn = this.element.querySelector('.contact-link-open');
    if (openBtn) {
      openBtn.addEventListener('click', () => this.handleOpen());
    }

    // Clear button click
    const clearBtn = this.element.querySelector('.contact-link-clear');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => this.handleClear());
    }
  }

  /**
   * Handle search button click - open ContactPicker modal
   */
  private async handleSearch(): Promise<void> {
    if (this.options.disabled) return;
    
    logger.debug('ContactLink search clicked', { id: this.options.id });

    try {
      const result = await ContactPicker.show({
        title: this.options.label ? `Select ${this.options.label}` : 'Select Contact',
        excludeContactIds: this.options.excludeContactIds,
        apiService: this.options.apiService
      });

      if (result.selected && result.contactId) {
        this.setContact(result.contactId, result.fullName || null);
        this.options.onContactChange?.(result.contactId, result.fullName || null);
        logger.info('Contact selected', { 
          id: this.options.id, 
          contactId: result.contactId, 
          fullName: result.fullName 
        });
      }
    } catch (error) {
      logger.error('ContactPicker failed', { error });
      ToasterService.error('Failed to open contact picker');
    }
  }

  /**
   * Handle open button click - open the contact record
   */
  private async handleOpen(): Promise<void> {
    if (!this.contactId) {
      ToasterService.info('No contact selected');
      return;
    }

    logger.debug('ContactLink open clicked', { 
      id: this.options.id, 
      contactId: this.contactId 
    });

    // Use custom handler if provided
    if (this.options.customOpenHandler) {
      try {
        await this.options.customOpenHandler(this.contactId);
        return;
      } catch (error) {
        logger.error('Custom open handler failed', { error });
        ToasterService.error('Failed to open contact');
        return;
      }
    }

    // Default: use OpenForm to navigate to contact detail
    if (typeof window.OpenForm === 'function') {
      window.OpenForm(`contactDetail?id=${this.contactId}`);
    } else {
      // Fallback: use hash navigation
      window.location.hash = `contactDetail?id=${this.contactId}`;
    }

    // Notify callback
    this.options.onContactOpen?.(this.contactId);
  }

  /**
   * Handle clear button click - remove the contact reference
   */
  private handleClear(): void {
    if (this.options.disabled || !this.contactId) return;

    logger.debug('ContactLink clear clicked', { 
      id: this.options.id, 
      previousContactId: this.contactId 
    });

    const previousId = this.contactId;
    const previousName = this.contactName;

    this.setContact(null, null);
    this.options.onContactChange?.(null, null);

    logger.info('Contact cleared', { 
      id: this.options.id, 
      previousContactId: previousId,
      previousName: previousName
    });
  }

  /**
   * Update button states based on current contact and disabled state
   */
  private updateButtonStates(): void {
    if (!this.element) return;

    const { disabled } = this.options;
    const hasContact = this.contactId !== null && this.contactId > 0;

    // Search button
    const searchBtn = this.element.querySelector('.contact-link-search') as HTMLButtonElement;
    if (searchBtn) {
      searchBtn.disabled = disabled || false;
    }

    // Open button - enable only when contact is selected
    const openBtn = this.element.querySelector('.contact-link-open') as HTMLButtonElement;
    if (openBtn) {
      const openDisabled = disabled || !hasContact;
      openBtn.dataset.disabled = openDisabled ? 'true' : 'false';
      openBtn.className = openBtn.className.replace(/opacity-60 cursor-not-allowed|hover:bg-base-200/g, '');
      openBtn.className += openDisabled ? ' opacity-60 cursor-not-allowed' : ' hover:bg-base-200';
      const icon = openBtn.querySelector('i');
      if (icon) {
        icon.style.color = openDisabled ? '#9ca3af' : '#3b82f6';
      }
    }

    // Clear button - enable only when contact is selected
    const clearBtn = this.element.querySelector('.contact-link-clear') as HTMLButtonElement;
    if (clearBtn) {
      const clearDisabled = disabled || !hasContact;
      clearBtn.dataset.disabled = clearDisabled ? 'true' : 'false';
      clearBtn.className = clearBtn.className.replace(/opacity-60 cursor-not-allowed|hover:bg-base-200/g, '');
      clearBtn.className += clearDisabled ? ' opacity-60 cursor-not-allowed' : ' hover:bg-base-200';
      const icon = clearBtn.querySelector('i');
      if (icon) {
        icon.style.color = clearDisabled ? '#9ca3af' : '#ef4444';
      }
    }
  }
}
